import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { ProtectedRoutes, AuthRoutes, MaintenanceRoute } from '../routes';
import { CLottie } from 'components/atoms';
import ReactGA from 'react-ga4';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import constants from 'utils/constants';
import { useEffect, useState } from 'react';
import CookieConsent from 'components/organisms/cookie-consent/Index';
import { initClarity } from '../clarity';
import { useWelcomeMutation } from 'services/pet-owner';

const isProd = window.location.href.includes('portal.vetamerikan.org');

const unvisibleGA =
  window.location.href.includes('localhost') ||
  window.location.href.includes('portaldev') ||
  window.location.href.includes('portaluat');
if (!unvisibleGA) {
  console.log('GA INIT');
  ReactGA.initialize('G-RZFWZJGF14');
}

export default function App() {
  const router = createBrowserRouter([...MaintenanceRoute, ...ProtectedRoutes, ...AuthRoutes]);
  const [connection, setConnection] = useState<HubConnection>();
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [isWelcomeLoaded, setIsWelcomeLoaded] = useState(false);
  const [welcome] = useWelcomeMutation();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  if (process.env.NODE_ENV === 'production') console.log = function () {};

  useEffect(() => {
    const fetchWelcomeData = async () => {
      try {
        const response = await welcome({
          token: '',
          username: '',
          password: '',
          expire_date: '',
        }).unwrap();

        constants.reCaptchaSiteKey = response.data?.recaptcha_site_key;
        constants.meetUrl = response.data?.meet_url;
        setIsWelcomeLoaded(true);
      } catch (err) {
        console.error('Failed to fetch welcome:', err);
        setIsWelcomeLoaded(true);
      }
    };

    fetchWelcomeData();
  }, [welcome]);

  function createHubConnection() {
    const connection = new HubConnectionBuilder()
      .withUrl(`${constants.baseUrl}/sessionhub`)
      .withAutomaticReconnect()
      // .configureLogging(LogLevel.Information)
      .build();

    setConnection(connection);
  }

  useEffect(() => {
    if (!haveConsent()) {
      setShowCookieConsent(true);
    } else {
      const consentData = JSON.parse(localStorage.getItem('cookie-consent'));
      if (consentData?.analytics) {
        initClarityOnProd();
      }
    }
  }, []);

  const haveConsent = () => {
    const consentData = JSON.parse(localStorage.getItem('cookie-consent'));
    const CONSENT_EXPIRATION_DAYS = 180;

    if (!consentData || isConsentExpired(consentData.timestamp, CONSENT_EXPIRATION_DAYS)) {
      return false;
    } else {
      return true;
    }
  };

  const isConsentExpired = (timestamp, expirationDays) => {
    const consentDate = new Date(timestamp);
    const expirationDate = new Date(consentDate);
    expirationDate.setDate(consentDate.getDate() + expirationDays);
    return new Date() > expirationDate;
  };

  const handleCookieConsent = (consentSettings) => {
    const consentData = {
      necessary: consentSettings.necessary,
      analytics: consentSettings.analytics,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    setShowCookieConsent(false);

    if (consentSettings.analytics) {
      initClarityOnProd();
    }
  };

  const initClarityOnProd = () => {
    if (isProd) {
      initClarity();
    }
  };

  useEffect(() => {
    if (connection) {
      (async () => {
        try {
          await connection.start();

          connection.on('IsMaintenance', function (appCode, status) {
            console.log('IsMaintenance', appCode, status);
            if (appCode === constants.applicationCode) {
              window.location.reload(); // reload the page, maintanence mode checking on the route file before loading the page
            }
          });
        } catch (error) {
          console.error(error);
        }
      })();
    } else {
      createHubConnection();
    }

    return () => {
      connection?.stop();
    };
  }, [connection]);

  if (!isWelcomeLoaded) {
    return <CLottie isFullPage containerStyle="h-screen" />;
  }

  return (
    <>
      <RouterProvider
        router={router}
        fallbackElement={<CLottie isFullPage containerStyle="h-screen" />}
      />
      {showCookieConsent && (
        <CookieConsent onAccept={handleCookieConsent} onReject={handleCookieConsent} />
      )}
    </>
  );
}

